import { useQuery } from '@tanstack/react-query'
import { getSpreadsAll } from '@/api/services/spreads.service'
import { getWpcauthForExternalApi } from '@/api/services/auth.service'
import { queryKeys } from '@/api/query-keys'
import type { Spread } from '@/api/types/spread'

/**
 * Stable sort by `spreadNumber` ascending; ties preserve API response order.
 */
export function sortSpreads(spreads: Spread[]): Spread[] {
  return [...spreads]
    .map((s, originalIndex) => ({ s, originalIndex }))
    .sort((a, b) => {
      const an = typeof a.s.spreadNumber === 'number' ? a.s.spreadNumber : Number.NaN
      const bn = typeof b.s.spreadNumber === 'number' ? b.s.spreadNumber : Number.NaN
      const aOk = Number.isFinite(an)
      const bOk = Number.isFinite(bn)

      if (aOk && bOk && an !== bn) return an - bn
      if (aOk && !bOk) return -1
      if (!aOk && bOk) return 1

      return a.originalIndex - b.originalIndex
    })
    .map(({ s }) => s)
}

export function getSpreadAtIndex(spreads: Spread[], spreadIndex: number): Spread | null {
  const ordered = sortSpreads(spreads)

  if (spreadIndex < 0 || spreadIndex >= ordered.length) return null

  return ordered[spreadIndex] ?? null
}

/**
 * Sorted-order index of the spread containing the given yearbook page number
 * (matches `SpreadPage.number`, not `Spread.spreadNumber`/index). Null if not found.
 */
export function findSpreadIndexForPageNumber(spreads: Spread[], pageNumber: number): number | null {
  const ordered = sortSpreads(spreads)
  const index = ordered.findIndex((spread) =>
    spread.pages?.some((page) => page.number === pageNumber)
  )

  return index === -1 ? null : index
}

export function useSpreadsQuery() {
  return useQuery({
    queryKey: queryKeys.spreads.all,
    queryFn: () => getSpreadsAll(),
    enabled: typeof window !== 'undefined' && Boolean(getWpcauthForExternalApi()),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

/** Derive editor page view mode from API pages on the active spread. */
export function pageViewModeFromSpreadPages(
  pages: Spread['pages']
): 'single-first' | 'single-last' | 'spread' {
  const list = pages ?? []

  if (list.length === 0) return 'spread'
  if (list.length === 1) {
    return list[0]?.leftPage === true ? 'single-last' : 'single-first'
  }

  return 'spread'
}

export {
  type SpreadPageIndicator,
  spreadPagesForMenubarIndicator,
} from '@/lib/spreads/spread-page-numbers'
